// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * What panelizable boards a `.kicad_pcb` file offers.
 *
 * A single file can hold many physically separate boards — the Guardian test
 * board carries 22 — and KiKit's way of naming them is a `kikit:Board`
 * footprint placed on the outline of each. This module reports both: every
 * annotated board, and any leftover closed outline that no annotation claimed.
 *
 * Ported from `analyze_source` in `sigmaKi.py:251-284`. The result feeds the
 * Sources panel and seeds `boards[]` entries when one is placed.
 */

import type { Board, BoardObject } from '../model/board.ts';
import type { BBox } from '../model/bbox.ts';
import type { Point } from '../model/geometry.ts';
import { getNums } from '../sexp/index.ts';
import {
  type Polygon,
  type Ring,
  extractOutlines,
  polygonBBox,
  ringsOf,
} from './outline.ts';

/** How far an annotation may sit from an edge and still count as pointing at it (mm). */
const ANNOTATION_TOLERANCE = 0.25;

export type BoardSource =
  | { type: 'auto' }
  | { type: 'annotation'; ref: string }
  | { type: 'rectangle'; tl: [number, number]; br: [number, number] };

export interface SourceItem {
  /** Stable within one file: `annotation:<ref>` or `outline:<n>`. */
  id: string;
  kind: 'annotation' | 'outline';
  /** Shown in the Sources list and used to seed `boards[].name`. */
  name: string;
  source: BoardSource;
  bbox: [number, number, number, number];
  outlines: Polygon[];
}

export interface SourceAnalysis {
  file: string;
  items: SourceItem[];
  warnings: string[];
}

export interface Annotation {
  ref: string;
  name: string;
  at: Point;
}

/**
 * Every KiKit board annotation in the file.
 *
 * The library identifier is matched case-insensitively on both halves: real
 * boards write `KiKit:Board` as often as `kikit:Board`, and a case-sensitive
 * comparison silently reports a file as having no annotations at all.
 */
export function listAnnotations(board: Board): Annotation[] {
  const found: Annotation[] = [];

  for (const o of board.objects as BoardObject[]) {
    if (o.type !== 'footprint') continue;
    const [lib, item] = (o.libId ?? '').split(':');
    if ((lib ?? '').toLowerCase() !== 'kikit') continue;
    if ((item ?? '').toLowerCase() !== 'board') continue;

    const at = getNums(o.node, 'at');
    if (at.length < 2) continue;

    const ref = o.reference ?? '';
    if (ref === '') continue;
    found.push({ ref, name: displayName(o, item ?? ''), at: [at[0]!, at[1]!] });
  }

  found.sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0));
  return disambiguate(found);
}

/**
 * The reference designator is an identifier; the Value field is a name. Prefer
 * the name when there is one, but ignore a Value that merely echoes the
 * footprint's own item name, which some KiCad versions substitute.
 */
function displayName(o: BoardObject, itemName: string): string {
  const value = (o.value ?? '').trim();
  if (value !== '' && value.toLowerCase() !== itemName.toLowerCase()) return value;
  return o.reference ?? '';
}

/** Two boards showing the same name would be indistinguishable in the list. */
function disambiguate(items: Annotation[]): Annotation[] {
  const counts = new Map<string, number>();
  for (const a of items) counts.set(a.name, (counts.get(a.name) ?? 0) + 1);
  return items.map((a) =>
    (counts.get(a.name) ?? 0) > 1 && a.name !== a.ref ? { ...a, name: `${a.name} (${a.ref})` } : a,
  );
}

// --- pointing at an outline ---------------------------------------------------------

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function distanceToRing(p: Point, ring: Ring): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const d = distanceToSegment(p, ring[i]!, ring[(i + 1) % ring.length]!);
    if (d < best) best = d;
  }
  return best;
}

/** Closest distance from a point to any contour of a polygon. */
export function distanceToPolygon(p: Point, poly: Polygon): number {
  let best = Infinity;
  for (const ring of ringsOf(poly)) {
    const d = distanceToRing(p, ring);
    if (d < best) best = d;
  }
  return best;
}

/**
 * The outline an annotation points at.
 *
 * KiKit's rule (`extractSourceAreaByAnnotation`, `kikit/panelize.py:2613`) is
 * that the annotation footprint's own position must lie *on* an Edge.Cuts
 * segment; the enclosing closed contour is then the board. So this is a
 * nearest-contour test, not an arrow trace.
 */
function outlineFor(at: Point, polygons: Polygon[]): { index: number; distance: number } | null {
  let best = -1;
  let bestDistance = Infinity;
  polygons.forEach((poly, i) => {
    const d = distanceToPolygon(at, poly);
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  });
  return best === -1 ? null : { index: best, distance: bestDistance };
}

function toTuple(b: BBox): [number, number, number, number] {
  return [round4(b.minX), round4(b.minY), round4(b.maxX), round4(b.maxY)];
}

function round4(v: number): number {
  return Math.round(v * 1e4) / 1e4;
}

// --- the analysis -------------------------------------------------------------------

export function analyzeSource(board: Board, file: string): SourceAnalysis {
  const { polygons, warnings } = extractOutlines(board);
  const items: SourceItem[] = [];
  const claimed = new Set<number>();

  for (const ann of listAnnotations(board)) {
    const hit = outlineFor(ann.at, polygons);
    if (hit === null || hit.distance > ANNOTATION_TOLERANCE) {
      // KiKit raises here. A whole file failing because one annotation was
      // nudged off its edge is not useful in an editor, so the board is still
      // offered through the `outline:N` fallback below and this is a warning.
      warnings.push(
        `Annotation '${ann.ref}' does not sit on a board edge` +
          (hit === null ? '' : ` (nearest is ${hit.distance.toFixed(3)} mm away)`) +
          '; ignored',
      );
      continue;
    }

    claimed.add(hit.index);
    const poly = polygons[hit.index]!;
    items.push({
      id: `annotation:${ann.ref}`,
      kind: 'annotation',
      name: ann.name,
      source: { type: 'annotation', ref: ann.ref },
      bbox: toTuple(polygonBBox(poly)),
      outlines: [poly],
    });
  }

  let n = 0;
  polygons.forEach((poly, i) => {
    if (claimed.has(i)) return;
    n += 1;
    const box = polygonBBox(poly);
    items.push({
      id: `outline:${n}`,
      kind: 'outline',
      name: `outline-${n}`,
      source: {
        type: 'rectangle',
        tl: [round4(box.minX), round4(box.minY)],
        br: [round4(box.maxX), round4(box.maxY)],
      },
      bbox: toTuple(box),
      outlines: [poly],
    });
  });

  return { file, items, warnings };
}

/** An SVG path for a set of outlines, used by the Sources list thumbnails. */
export function outlinesToPath(outlines: Polygon[]): string {
  const ring = (r: Ring): string =>
    `M ${r.map(([x, y]) => `${round4(x)},${round4(y)}`).join(' L ')} Z`;
  return outlines.flatMap((p) => ringsOf(p).map(ring)).join(' ');
}

// --- resolving a spec's `boards[].source` -------------------------------------------

/**
 * The source area and outlines a `boards[]` entry selects from one file.
 *
 * Mirrors `resolve_source_area` (`sigmaKi.py:169-188`) followed by the
 * intersection test in `compute_preview`: the area picks a box, and the board is
 * every closed outline that box touches. The placement anchor refers to the
 * **area**, not to the outlines, which is why both come back.
 */
export function resolveSource(
  polygons: Polygon[],
  annotations: Annotation[],
  source: BoardSource | undefined,
  edgeWidth = 0,
): { outlines: Polygon[]; bounds: BBox; edgeWidth: number } | null {
  const spec = source ?? { type: 'auto' };
  let area: BBox | null = null;

  if (spec.type === 'auto') {
    area = boundsOf(polygons);
  } else if (spec.type === 'annotation') {
    const ann = annotations.find((a) => a.ref === spec.ref);
    if (ann === undefined) return null;
    const hit = polygons.findIndex((p) => distanceToPolygon(ann.at, p) <= ANNOTATION_TOLERANCE);
    if (hit === -1) return null;
    area = polygonBBox(polygons[hit]!);
  } else if (spec.type === 'rectangle') {
    const [tlx, tly] = spec.tl;
    const [brx, bry] = spec.br;
    area = { minX: tlx, minY: tly, maxX: brx, maxY: bry };
  }

  if (area === null) return null;
  const box = area;
  const outlines = polygons.filter((p) => boxesTouch(polygonBBox(p), box));
  return outlines.length === 0 ? null : { outlines, bounds: box, edgeWidth };
}

function boundsOf(polygons: Polygon[]): BBox | null {
  let out: BBox | null = null;
  for (const p of polygons) {
    const b = polygonBBox(p);
    out = out === null
      ? b
      : {
          minX: Math.min(out.minX, b.minX),
          minY: Math.min(out.minY, b.minY),
          maxX: Math.max(out.maxX, b.maxX),
          maxY: Math.max(out.maxY, b.maxY),
        };
  }
  return out;
}

function boxesTouch(a: BBox, b: BBox): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}
